import { useEffect, useState } from "react";
import api from "./api";

/**
 * Seeds a calculator's interest-rate default from the live 10y-fixed band in
 * rates.json (GET /rates), falling back to the caller's own constant.
 *
 * Why the fallback is not optional: rates.json is maintained by hand and has
 * no guaranteed update cadence, so the backend stamps every response with
 * `stale` — true for any of the three reasons it reports as `stale_reason`:
 * `no_date` (unreadable or future `as_of`), `age` (older than
 * RATES_MAX_AGE_DAYS), or `drift` (the 10y benchmark has moved further than
 * RATES_MAX_SWAP_DRIFT since the band was set). Note the third: a band only
 * days old can arrive stale. A stale band is treated exactly like a
 * missing one — seeding a calculator from a year-old rate would be the same
 * defect as the hardcoded BANKZINS 4.50% it replaced, only harder to spot
 * because it looks live (LL-31).
 *
 * Returns { rate, asOf, stale, source }:
 *   source "rates" → live band midpoint;  "fallback" → the caller's constant.
 * `rate` is never null, so callers can seed state unconditionally.
 */
export function useRateDefault(fallbackRate) {
  const [state, setState] = useState({
    rate: fallbackRate, asOf: null, stale: null, source: "fallback",
  });

  useEffect(() => {
    let cancelled = false;
    api.get("/rates").then(({ data }) => {
      if (cancelled) return;
      const band = data?.rates?.fix_10y_privat;
      const usable = data?.available && !data?.stale
        && typeof band?.low === "number" && typeof band?.high === "number";
      if (!usable) {
        setState({
          rate: fallbackRate,
          asOf: data?.rates?.as_of ?? null,
          stale: data?.stale ?? true,
          source: "fallback",
        });
        return;
      }
      setState({
        rate: (band.low + band.high) / 2,
        asOf: data.rates.as_of ?? null,
        stale: false,
        source: "rates",
      });
    }).catch(() => {});   // keep the fallback already in state
    return () => { cancelled = true; };
  }, [fallbackRate]);

  return state;
}

/**
 * Benchmark drift as signed basis points: 0.008 → "+80", -0.0033 → "-33".
 *
 * The sign is the content, not decoration — a benchmark that rose 80bp and one
 * that fell 80bp invalidate the band in opposite directions, and the reader is
 * deciding whether their quote is too cheap or too dear.
 */
export function signedBp(drift) {
  const bp = Math.round((drift ?? 0) * 10000);
  return `${bp > 0 ? "+" : ""}${bp}`;
}

/**
 * Why a band is not usable as the current market rate, in the reader's words.
 * `ns` picks the i18n namespace — "shell" for the top bar, "briefs" for the
 * Zins-Puls panel; both render the same three reasons at different lengths.
 *
 * Written as an explicit map with a NEUTRAL default rather than an if/else
 * chain ending in the age message. The age branch used to be that `else`, so
 * `no_date` rendered as "band — days old" and named a cause it did not know.
 * A fourth reason added to rates_stale_reason() later lands on the neutral
 * text instead of inheriting the same bug.
 */
export function staleNote(r, t, ns) {
  switch (r?.stale_reason) {
    case "drift":   return t(`${ns}.zinsStaleDrift`, { bp: signedBp(r.swap_drift) });
    case "no_date": return t(`${ns}.zinsStaleNoDate`);
    case "age":     return t(`${ns}.zinsStaleAge`, { days: r.age_days ?? "—" });
    default:        return t(`${ns}.zinsStaleGeneric`);
  }
}

/** Field hint: "Marktzins 3,50 % (Stand 19.08.2026)" or the fallback note. */
export function rateHint(rd, t) {
  const pct = `${(rd.rate * 100).toFixed(2).replace(".", ",")} %`;
  if (rd.source !== "rates") return t("rates.hintFallback", { rate: pct });
  const d = rd.asOf ? new Date(rd.asOf).toLocaleDateString("de-AT") : "—";
  return t("rates.hintLive", { rate: pct, date: d });
}
